/**
 * custInqParser — ports the legacy SPFx `JxChangeService.custInq` parser 1:1
 * into the v3 RCM module.
 *
 * Source of truth:
 *   C:\Projects\retail-account-opening\spfx-account-opening-v2\
 *     pcb-account-opening-v2\src\webparts\accountOpeningV2\services\
 *       JxChangeService.ts  (lines 170-303, interface `CustInqResult`)
 *
 * Background:
 * -----------
 * The v3 Function `/accounts/cust-inq/{cifId}` forwards to Core Director's
 * `Customer/CustInq` operation and returns
 *
 *     { Success:true, Data: <raw-jXRL-JSON>, Error:null, ... }
 *
 * where the raw jXRL JSON has the nested shape
 *
 *     {
 *       "CustRec": {
 *         "CustId": "992876543",
 *         "CustDetail": {
 *           "PersonName":  { FirstName, MiddleName, LastName, ComName, Salutation },
 *           "TaxId":       "123456789" | { "#text": "123456789", "@MaskVal": "XXX-XX-6789" },
 *           "Addr":        { StreetAddr1, City, StateCode, PostalCode, ... },
 *           "MailAddr":    { ... },
 *           "PhoneArray":  [ { PhoneType, PhoneNum } ]  (or singleton object),
 *           "EmailArray":  [ { EmailAddr } ],
 *           "BirthDt":     "YYYY-MM-DD",
 *           "CustType":    "I" | "Business",
 *           "EmplName", "OccType", "StdIndustCode" / "NAICSCode",
 *           "Gender", "InsiderCode" (via x_BusDetail),
 *           ...
 *         },
 *         "IntnetFinInstIdArray": [ { IntnetFinInstId } ]
 *       },
 *       "x_IdVerify": {
 *         "IdVerifyArray": [ {
 *           IdVerifyCode, IdVerifyVal, IdIssueBy,
 *           IdVerifyQueryArray: [ { IdVerifyQuery:"IssDt"|"ExpDt", IdVerifyQueryVal:"YYYY-MM-DD" } ]
 *         } ]
 *       }
 *     }
 *
 * Prior v3 bug: components read `CustInqRs_MType.CustRec_CType` (that wrapper
 * does not exist in jXRL) and flat strings like `rec.TaxId` / `rec.FirstName`
 * (the real shape is nested), so every KV rendered "—".
 *
 * This parser mirrors the legacy logic *exactly*:
 *   - `txt()` unwraps string-or-`{#text,...}` objects (masked TaxId, etc.)
 *   - Phones loop over PhoneArray looking for H/C/B type codes (falls back to
 *     first phone for Home if untyped).
 *   - Emails take the first EmailArray entry.
 *   - NetTeller id from IntnetFinInstIdArray[0].IntnetFinInstId.
 *   - TINCode derives from TINInfo/TaxDetail or CustType ("I" → "S").
 *   - Gender normalizes M|Male|F|Female → "Male"|"Female".
 *   - InsiderCode falls back to `EmployeeCode` then `'0'` (confirmed missing
 *     in CustInq response for many CIFs — see memory/custinq-limitations.md).
 *   - x_IdVerify is a **sibling** of CustRec, NOT inside CustRec.
 *
 * Pure — no React, no side effects. Export surface is the parsed shape
 * `ParsedCustInq` plus the `parseCustInq(raw)` entry point.
 */

import { txt, hasJxrlError, getJxrlErrorCode } from '@/utils/jxrl';

// -----------------------------------------------------------------------------
// Parsed shape — matches legacy `CustInqResult` 1:1 (field names / types).
// -----------------------------------------------------------------------------

export interface ParsedCustInq {
  /** True when the parser found a populated `CustRec`. False on error/empty. */
  success: boolean;
  /** Core Director CIF id echo — same as the input param unless jXRL overrides. */
  cifId: string;

  // Demographics (from CustDetail.PersonName + CustDetail direct fields)
  firstName: string;
  middleName: string;
  lastName: string;
  comName: string;           // Business entity name (ComName)
  salutation: string;        // Field 5 — "KILL" signals System Ops delete flag
  suffix: string;
  gender: string;            // Normalized: 'Male' | 'Female' | ''
  dob: string;               // BirthDt as delivered (YYYY-MM-DD)

  // Identifiers
  taxId: string;             // SSN / EIN — unwrapped from {#text,@MaskVal}
  tinCode: string;           // 'S' | 'E'
  customerType: string;      // CustType (I, Business, etc.)
  insiderCode: string;       // Field 21 Employee Code — '' when not read

  // Contact
  phoneHome: string;
  phoneCell: string;
  phoneBus: string;
  email: string;

  // Primary address (CustDetail.Addr = Core Director fields 9-12). At PCB this
  // is ALWAYS the mailing address; when secondary* below is blank it is the
  // physical too (Bill 2026-09-29).
  addr: string;              // StreetAddr1
  city: string;
  state: string;             // StateCode (2-letter)
  zip: string;               // PostalCode

  // Secondary address — Core Director UDF fields 86-89 ("2nd Address"): PCB's
  // PHYSICAL address, populated only when it differs from the mailing address
  // in 9-12. Lives in x_BusDetail like the 2nd ID, so it is blank unless the
  // caller passed ?includeUdf=true. Core's dictionary calls 86-89 "Secondary ...
  // for Mailing purposes"; PCB practice is the reverse and Bill ruled to honor
  // the practice. There is NO CustDetail.SecondaryAddr / Addr2 element — the
  // hydration used to look for one and never found it.
  secondaryAddr: string;     // F86
  secondaryCity: string;     // F87
  secondaryState: string;    // F88
  secondaryZip: string;      // F89

  // Employment
  employer: string;          // EmplName
  occupation: string;        // OccType
  naicsCode: string;         // StdIndustCode / NAICSCode (trimmed)
  employmentStatus: string;  // EmplStat (single char: F=full P=part R=retired S=student U=unemployed) — Bill 2026-04-26: existing CIFs were missing this on dialog reload because the field wasn't being parsed.

  // NetTeller enrollment
  existingNetTellerId: string;

  // CIP (x_IdVerify sibling element, fields 254-258)
  idTypeCode: string;        // IdVerifyCode ("1"=DL etc.)
  idNumber: string;          // IdVerifyVal
  idState: string;           // IdIssueBy
  idIssueDate: string;       // YYYY-MM-DD
  idExpiryDate: string;

  // ── 2nd ID (PCB CUSTOM fields, x_BusDetail.BusDetail.UserDefInfoArray) ──
  // NOT part of Core Director's CIP block. Bill 2026-07-15: "800s series fields
  // are not associated with ID/CIP - they are custom fields PCB created in the
  // past." They are UDFs PCB defined, written by the Function via
  // BusDetail.UserDefInfoArray — so the read mirrors the write.
  //
  // Only populated when the caller asked for them (`?includeUdf=true`); the
  // x_BusDetail block is ~100 KB and jXchange returns only ONE extension element
  // per call, so it costs a second round-trip. Empty string when absent, which is
  // also the normal state — most customers have no 2nd ID.
  //
  // Dates are normalized to YYYY-MM-DD here. On the wire they arrive as raw
  // YYYYMMDD ("20210101") — a different shape from x_IdVerify's ISO dates.
  idTypeCode2: string;       // F814 — same IdVerifyCode enum as F254
  idNumber2: string;         // F801
  idIssuer2: string;         // F813
  idIssueDate2: string;      // F815 → YYYY-MM-DD
  idExpiryDate2: string;     // F816 → YYYY-MM-DD

  // Citizenship (CustDetail.CitizenshipCountry / Field 356) — single field
  // present on every CIF. Empty string when not populated.
  countryOfCitizenship: string;

  // Alien_CType (CustDetail.TaxDetail.Alien_CType) — populated only for
  // NRA CIFs. All fields empty string when the CIF is a US citizen
  // (TINCode != 'Forn'). See reference_jxchange_alien_ctype_capability_20260427.md
  // for the full FATCA / Chapter-3+4 schema. Wave 1 #2 NRA path
  // (Bill 2026-04-28): extend hydration to populate the Individual NRA
  // section when CustInq returns these fields on an existing NRA CIF.
  alienCitizenshipCountry: string;    // Alien_CType.CitzCntryType (ISO alpha-2)
  alienTaxResidencyCountry: string;   // Alien_CType.CountryOfTaxResidency
  alienFornTaxId: string;             // Alien_CType.FornTaxId — foreign tax ID / passport
  alienVisaType: string;              // F-1 / J-1 / H-1B / etc.
  alienVisaExpDt: string;             // YYYY-MM-DD
  alienI94Number: string;             // 11-digit I-94
  alienI94ExpDt: string;              // YYYY-MM-DD
  alienPassportNumber: string;
  alienPassportCountry: string;       // ISO alpha-2 issuing country
  alienW8Type: string;                // BEN | BEN-E | ECI | EXP | IMY
  alienLastW8Dt: string;              // YYYY-MM-DD
  alienW8ExpDt: string;               // YYYY-MM-DD
  alienIRSRptType: string;            // 1042S | 1099INT | NoRpt
  alienIRSRecipCode: string;
  alienIRSExmpCode: string;
  alienGiin: string;
  // Foreign residence (separate from US mailing address)
  alienResAddr: string;
  alienResCity: string;
  alienResState: string;
  alienResPostal: string;
  alienResCountry: string;            // ISO alpha-2

  /** Error string when `success=false`, else empty. */
  error: string;

  /** Raw Data echo for components that want the full passthrough (rare). */
  raw: unknown;
}

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

/**
 * Lift the jXRL raw `Data` out of any envelope the Function might deliver.
 *
 *   Envelope shapes we tolerate (in order):
 *     1. `{Success,Data:{CustRec:...}}`      — current Function
 *     2. `{success,data:{CustRec:...}}`      — legacy casing (SPFx path)
 *     3. `{Customer:{CustRec:...}}`          — older envelope variant
 *     4. Raw object already at `{CustRec:...}` (no envelope)
 */
function extractDataNode(input: unknown): Record<string, unknown> | null {
  if (!isPlainObject(input)) return null;

  // 1) PascalCase envelope
  if (isPlainObject(input['Data']) && isPlainObject((input['Data'] as Record<string, unknown>)['CustRec'])) {
    return input['Data'] as Record<string, unknown>;
  }
  // 2) camelCase envelope
  if (isPlainObject(input['data']) && isPlainObject((input['data'] as Record<string, unknown>)['CustRec'])) {
    return input['data'] as Record<string, unknown>;
  }
  // 3) legacy Customer key
  if (isPlainObject(input['Customer']) && isPlainObject((input['Customer'] as Record<string, unknown>)['CustRec'])) {
    return input['Customer'] as Record<string, unknown>;
  }
  // 4) already unwrapped
  if (isPlainObject(input['CustRec'])) {
    return input;
  }
  // Some callers hand us the envelope without CustRec populated — still
  // return `Data` as a best-effort so the error path can report the failure.
  if (isPlainObject(input['Data'])) return input['Data'] as Record<string, unknown>;
  if (isPlainObject(input['data'])) return input['data'] as Record<string, unknown>;
  return input;
}

/** Return a blank parsed shape with the supplied cifId echo + error string. */
function emptyResult(cifId: string, error: string, raw: unknown): ParsedCustInq {
  return {
    success: false,
    cifId,
    firstName: '',
    middleName: '',
    lastName: '',
    comName: '',
    salutation: '',
    suffix: '',
    gender: '',
    dob: '',
    taxId: '',
    tinCode: '',
    customerType: '',
    insiderCode: '0',
    phoneHome: '',
    phoneCell: '',
    phoneBus: '',
    email: '',
    addr: '',
    city: '',
    state: '',
    zip: '',
    secondaryAddr: '',
    secondaryCity: '',
    secondaryState: '',
    secondaryZip: '',
    employer: '',
    occupation: '',
    naicsCode: '',
    employmentStatus: '',
    existingNetTellerId: '',
    idTypeCode: '',
    idNumber: '',
    idState: '',
    idIssueDate: '',
    idExpiryDate: '',
    idTypeCode2: '',
    idNumber2: '',
    idIssuer2: '',
    idIssueDate2: '',
    idExpiryDate2: '',
    countryOfCitizenship: '',
    alienCitizenshipCountry: '',
    alienTaxResidencyCountry: '',
    alienFornTaxId: '',
    alienVisaType: '',
    alienVisaExpDt: '',
    alienI94Number: '',
    alienI94ExpDt: '',
    alienPassportNumber: '',
    alienPassportCountry: '',
    alienW8Type: '',
    alienLastW8Dt: '',
    alienW8ExpDt: '',
    alienIRSRptType: '',
    alienIRSRecipCode: '',
    alienIRSExmpCode: '',
    alienGiin: '',
    alienResAddr: '',
    alienResCity: '',
    alienResState: '',
    alienResPostal: '',
    alienResCountry: '',
    error,
    raw,
  };
}

/**
 * Extract Alien_CType complex from `CustDetail.TaxDetail.Alien_CType` per
 * jXchange schema (memory `reference_jxchange_alien_ctype_capability_20260427`).
 * Every field returns empty string when not populated, so callers can
 * spread the result into form state without the NRA section flickering on
 * for non-NRA CIFs.
 *
 * Foreign residence comes back from `Alien_CType.ResAddr` as a sub-object
 * with StreetAddr1 / City / StateCode / PostalCode / Cntry — same shape as
 * the top-level Addr block.
 */
function pickAlienCType(detail: Record<string, unknown>): {
  countryOfCitizenship: string;
  alienCitizenshipCountry: string;
  alienTaxResidencyCountry: string;
  alienFornTaxId: string;
  alienVisaType: string;
  alienVisaExpDt: string;
  alienI94Number: string;
  alienI94ExpDt: string;
  alienPassportNumber: string;
  alienPassportCountry: string;
  alienW8Type: string;
  alienLastW8Dt: string;
  alienW8ExpDt: string;
  alienIRSRptType: string;
  alienIRSRecipCode: string;
  alienIRSExmpCode: string;
  alienGiin: string;
  alienResAddr: string;
  alienResCity: string;
  alienResState: string;
  alienResPostal: string;
  alienResCountry: string;
} {
  // Top-level CitizenshipCountry (Field 356) is independent of Alien_CType
  // and may be present on US-citizen CIFs (e.g. dual-citizen case).
  const countryOfCitizenship = txt(detail['CitizenshipCountry']);

  const blank = {
    countryOfCitizenship,
    alienCitizenshipCountry: '',
    alienTaxResidencyCountry: '',
    alienFornTaxId: '',
    alienVisaType: '',
    alienVisaExpDt: '',
    alienI94Number: '',
    alienI94ExpDt: '',
    alienPassportNumber: '',
    alienPassportCountry: '',
    alienW8Type: '',
    alienLastW8Dt: '',
    alienW8ExpDt: '',
    alienIRSRptType: '',
    alienIRSRecipCode: '',
    alienIRSExmpCode: '',
    alienGiin: '',
    alienResAddr: '',
    alienResCity: '',
    alienResState: '',
    alienResPostal: '',
    alienResCountry: '',
  };

  const taxDetail = isPlainObject(detail['TaxDetail']) ? detail['TaxDetail'] : null;
  if (!taxDetail) return blank;
  const alien = isPlainObject(taxDetail['Alien_CType']) ? taxDetail['Alien_CType'] : null;
  if (!alien) return blank;

  const resAddr = isPlainObject(alien['ResAddr']) ? alien['ResAddr'] : {};

  return {
    countryOfCitizenship,
    alienCitizenshipCountry: txt(alien['CitzCntryType']) || txt(alien['CitizenshipCountry']),
    alienTaxResidencyCountry: txt(alien['CountryOfTaxResidency']),
    alienFornTaxId: txt(alien['FornTaxId']),
    alienVisaType: txt(alien['VisaType']),
    alienVisaExpDt: txt(alien['VisaExpDt']),
    alienI94Number: txt(alien['I94Number']),
    alienI94ExpDt: txt(alien['I94ExpDt']),
    alienPassportNumber: txt(alien['PassportNumber']),
    alienPassportCountry: txt(alien['PassportCountry']),
    alienW8Type: txt(alien['AlienW8Type']) || txt(alien['W8Type']),
    alienLastW8Dt: txt(alien['LastW8Dt']),
    alienW8ExpDt: txt(alien['W8ExpDt']),
    alienIRSRptType: txt(alien['IRSRptType']),
    alienIRSRecipCode: txt(alien['IRSRecipCode']),
    alienIRSExmpCode: txt(alien['IRSExmpCode']),
    alienGiin: txt(alien['Giin']) || txt(alien['GIIN']),
    alienResAddr: txt(resAddr['StreetAddr1']) || txt(resAddr['Addr']),
    alienResCity: txt(resAddr['City']),
    alienResState: txt(resAddr['StateCode']) || txt(resAddr['State']),
    alienResPostal: txt(resAddr['PostalCode']) || txt(resAddr['Zip']),
    alienResCountry: txt(resAddr['Cntry']) || txt(resAddr['Country']),
  };
}

/** Extract the best `PhoneNum` for Home / Cell / Bus from a PhoneArray shape. */
function pickPhones(phonesRaw: unknown): { phoneHome: string; phoneCell: string; phoneBus: string } {
  let phoneHome = '';
  let phoneCell = '';
  let phoneBus = '';
  const list = Array.isArray(phonesRaw) ? phonesRaw : phonesRaw ? [phonesRaw] : [];
  for (const entry of list) {
    if (!isPlainObject(entry)) continue;
    const num = txt(entry['PhoneNum']);
    if (!num) continue;
    const typeVal = typeof entry['PhoneType'] === 'string' ? entry['PhoneType'] : '';
    if (typeVal === 'H' || typeVal === 'Home') {
      if (!phoneHome) phoneHome = num;
    } else if (typeVal === 'C' || typeVal === 'HomeCell' || typeVal === 'Cell') {
      if (!phoneCell) phoneCell = num;
    } else if (typeVal === 'B' || typeVal === 'Bus') {
      if (!phoneBus) phoneBus = num;
    } else if (!phoneHome) {
      phoneHome = num;
    }
  }
  return { phoneHome, phoneCell, phoneBus };
}

/** Extract first non-empty EmailAddr from an EmailArray shape. */
function pickEmail(emailsRaw: unknown): string {
  const list = Array.isArray(emailsRaw) ? emailsRaw : emailsRaw ? [emailsRaw] : [];
  for (const entry of list) {
    if (!isPlainObject(entry)) continue;
    const e = txt(entry['EmailAddr']);
    if (e) return e;
  }
  return '';
}

/** Extract the first NetTeller id from IntnetFinInstIdArray. */
function pickNetTellerId(arrRaw: unknown): string {
  const list = Array.isArray(arrRaw) ? arrRaw : arrRaw ? [arrRaw] : [];
  for (const entry of list) {
    if (!isPlainObject(entry)) continue;
    const id = txt(entry['IntnetFinInstId']);
    if (id) return id;
  }
  return '';
}

/**
 * Parse the x_IdVerify extend-element into flat idTypeCode/idNumber/idState/
 * idIssueDate/idExpiryDate. Handles the IdVerifyArray as array or singleton
 * object, and the nested IdVerifyQueryArray for IssDt / ExpDt.
 */
/**
 * Normalize a UDF date to YYYY-MM-DD.
 *
 * x_BusDetail returns these RAW as written — `"20210101"` — unlike x_IdVerify,
 * which returns ISO (`"2026-01-23"`). Same logical field, different shape per
 * container, so this cannot be shared with the CIP path. Tolerates an
 * already-ISO value in case jXchange ever normalizes. Anything else → '' rather
 * than a half-parsed date in a compliance field.
 */
function udfDateToIso(raw: unknown): string {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (!m) return '';
  // CD writes 0-dates ("00000000") for "not set" — those are not dates.
  if (m[1] === '0000' || m[2] === '00' || m[3] === '00') return '';
  return `${m[1]}-${m[2]}-${m[3]}`;
}

/**
 * Pull PCB's custom 2nd-ID UDFs out of x_BusDetail.
 *
 * Shape: x_BusDetail.BusDetail.UserDefInfoArray.UserDefInfo[] where each entry is
 * { UserDefTxt: "<field number>", UserDefCode: "<value>" }. ~222 entries come
 * back; we want five. Tolerates the array-vs-singleton shape jXchange uses
 * elsewhere, and reads the field number as a STRING (they arrive quoted).
 */
/**
 * x_BusDetail.BusDetail.UserDefInfoArray.UserDefInfo[] -> Map<field number, value>.
 * Field numbers arrive quoted and space-padded ("  5", " 86"); trimmed here so
 * callers key by the bare number. Empty map when the extension is absent.
 */
function udfMap(xBusDetail: unknown): Map<string, string> {
  const byField = new Map<string, string>();
  if (!isPlainObject(xBusDetail)) return byField;
  const bd = xBusDetail['BusDetail'];
  if (!isPlainObject(bd)) return byField;
  const raw = bd['UserDefInfoArray'];
  const container = isPlainObject(raw) ? raw['UserDefInfo'] : raw;
  const list = Array.isArray(container) ? container : container ? [container] : [];
  for (const e of list) {
    if (!isPlainObject(e)) continue;
    const f = txt(e['UserDefTxt']).trim();
    if (f) byField.set(f, txt(e['UserDefCode']).trim());
  }
  return byField;
}

/** UDF 86-89 — the physical address when 9-12 is the mailing (Bill 2026-09-29). */
function pickSecondaryAddress(xBusDetail: unknown): {
  secondaryAddr: string;
  secondaryCity: string;
  secondaryState: string;
  secondaryZip: string;
} {
  const byField = udfMap(xBusDetail);
  return {
    secondaryAddr:  byField.get('86') ?? '',
    secondaryCity:  byField.get('87') ?? '',
    secondaryState: byField.get('88') ?? '',
    secondaryZip:   byField.get('89') ?? '',
  };
}

function pickSecondId(xBusDetail: unknown): {
  idTypeCode2: string;
  idNumber2: string;
  idIssuer2: string;
  idIssueDate2: string;
  idExpiryDate2: string;
} {
  const blank = { idTypeCode2: '', idNumber2: '', idIssuer2: '', idIssueDate2: '', idExpiryDate2: '' };
  const byField = udfMap(xBusDetail);
  if (byField.size === 0) return blank;

  return {
    idTypeCode2:   byField.get('814') ?? '',
    idNumber2:     byField.get('801') ?? '',
    idIssuer2:     byField.get('813') ?? '',
    idIssueDate2:  udfDateToIso(byField.get('815')),
    idExpiryDate2: udfDateToIso(byField.get('816')),
  };
}

function pickIdVerify(xIdVerify: unknown): {
  idTypeCode: string;
  idNumber: string;
  idState: string;
  idIssueDate: string;
  idExpiryDate: string;
} {
  const blank = { idTypeCode: '', idNumber: '', idState: '', idIssueDate: '', idExpiryDate: '' };
  if (!isPlainObject(xIdVerify)) return blank;
  const raw = xIdVerify['IdVerifyArray'];
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  if (list.length === 0) return blank;
  const first = list[0];
  if (!isPlainObject(first)) return blank;

  const idTypeCode = txt(first['IdVerifyCode']);
  const idNumber   = txt(first['IdVerifyVal']);
  const idState    = txt(first['IdIssueBy']);

  let idIssueDate = '';
  let idExpiryDate = '';
  const qRaw = first['IdVerifyQueryArray'];
  const qList = Array.isArray(qRaw) ? qRaw : qRaw ? [qRaw] : [];
  for (const q of qList) {
    if (!isPlainObject(q)) continue;
    const info = isPlainObject(q['IdVerifyQueryInfo']) ? q['IdVerifyQueryInfo'] : q;
    const key = typeof info['IdVerifyQuery'] === 'string' ? info['IdVerifyQuery'] : '';
    const val = typeof info['IdVerifyQueryVal'] === 'string' ? info['IdVerifyQueryVal'] : '';
    if (key === 'IssDt') idIssueDate = val;
    else if (key === 'ExpDt') idExpiryDate = val;
  }

  return { idTypeCode, idNumber, idState, idIssueDate, idExpiryDate };
}

// -----------------------------------------------------------------------------
// Public entry point
// -----------------------------------------------------------------------------

/**
 * Parse a raw Function `/accounts/cust-inq/{cifId}` response into a flat
 * `ParsedCustInq`. Never throws — returns `success:false` with an error
 * string on any failure mode.
 *
 * @param raw   Whatever the api-client / fetch returned. Undefined-safe.
 * @param cifId The CIF id that was queried — used for echo + error context.
 */
/**
 * JHA CustType short-code → canonical long-form CustomerType.
 * Mirrors `modules/RAO/utils/normalizeCustomerType.ts` SHORT_TO_LONG —
 * inlined here so the utils/ parser doesn't depend on a RAO-module file.
 * Long-form input passes through unchanged.
 */
function normalizeCustomerTypeShortCode(raw: string): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '';
  // Short codes (≤2 chars) — map per observed JHA values at PCB.
  if (trimmed.length <= 2) {
    if (trimmed === 'I') return 'Individual';
    if (trimmed === 'B') return 'Corporation';
    if (trimmed === 'M') return 'Minor';
    if (trimmed === 'T') return 'RevocableTrust';
    if (trimmed === 'E') return 'Estate';
    return trimmed; // unknown short code — pass through, cohort dialog refines
  }
  // JHA's catch-all 'Business' label collapses to Corporation;
  // cohort dialog refines to LLC / Partnership / DBA after intake.
  if (trimmed === 'Business') return 'Corporation';
  return trimmed; // already long-form (Individual / RevocableTrust / etc.)
}

export function parseCustInq(raw: unknown, cifId: string): ParsedCustInq {
  if (raw === null || raw === undefined) {
    return emptyResult(cifId, 'CustInq returned no payload', raw);
  }

  // Explicit Success=false on the envelope → short-circuit.
  if (isPlainObject(raw)) {
    const succ = raw['Success'];
    if (succ === false) {
      const err = typeof raw['Error'] === 'string' ? (raw['Error'] as string)
                : typeof raw['ErrorMessage'] === 'string' ? (raw['ErrorMessage'] as string)
                : typeof raw['error'] === 'string' ? (raw['error'] as string)
                : 'CustInq failed';
      return emptyResult(cifId, err, raw);
    }
  }

  // jXRL-level error (Details envelope, MsgRsHdr ErrCode, etc.)
  if (hasJxrlError(raw)) {
    const code = getJxrlErrorCode(raw) ?? 'ERR';
    return emptyResult(cifId, 'jXRL error ' + code, raw);
  }

  const data = extractDataNode(raw);
  if (!data) {
    return emptyResult(cifId, 'CustInq payload not understood', raw);
  }

  const custRec = isPlainObject(data['CustRec']) ? data['CustRec'] : null;
  if (!custRec) {
    return emptyResult(cifId, 'CustInq response missing CustRec', raw);
  }

  const detail   = isPlainObject(custRec['CustDetail'])       ? custRec['CustDetail']         : {};
  const pn       = isPlainObject(detail['PersonName'])        ? detail['PersonName']          : {};
  const addrObj  = isPlainObject(detail['Addr'])              ? detail['Addr']                : {};
  const phones   = detail['PhoneArray'] ?? detail['PhoneInfo'] ?? detail['Phone'];
  const emails   = detail['EmailArray'] ?? detail['EmailInfo'] ?? detail['Email'];
  const ntArray  = custRec['IntnetFinInstIdArray']            ?? {};

  const { phoneHome, phoneCell, phoneBus } = pickPhones(phones);
  const email                              = pickEmail(emails);
  const netTellerId                        = pickNetTellerId(ntArray);

  // TINCode — TINInfo / TaxDetail, else infer from CustType
  const tinInfo = isPlainObject(detail['TINInfo']) ? detail['TINInfo']
                 : isPlainObject(detail['TaxDetail']) ? detail['TaxDetail']
                 : {};
  const custType = txt(detail['CustType']);
  const tinCode = txt(tinInfo['TINCode']) || txt(tinInfo['TinCode']) || (custType === 'I' ? 'S' : 'E');

  // Gender — Field 348, normalize M|F|Male|Female
  // Some jXRL responses (notably SPFx-era CIFs) emit Gender under x_BusDetail
  // rather than at the top of CustDetail. Check both paths before falling back.
  const xBusDetail = isPlainObject(detail['x_BusDetail']) ? detail['x_BusDetail'] : {};
  const rawGender = txt(detail['Gender']) || txt(xBusDetail['Gender']);
  const gender = rawGender === 'M' || rawGender === 'Male' ? 'Male'
                : rawGender === 'F' || rawGender === 'Female' ? 'Female'
                : '';

  // Field 21 Employee Code = x_BusDetail.BusDetail.InsiderCode (the merged CustInq grafts x_BusDetail at the top
  // level). Bill 2026-10-17: the old read looked only under CustDetail, never found it, and showed "0 Non-Employee" for
  // an employee whose Core Director value is 7. Blank (unknown) when x_BusDetail wasn't requested — never a guessed 0.
  const busRoot = isPlainObject(data['x_BusDetail']) ? data['x_BusDetail'] : null;
  const busDetail = busRoot && isPlainObject(busRoot['BusDetail']) ? busRoot['BusDetail'] : busRoot;
  const insiderCode = (busDetail ? txt(busDetail['InsiderCode']) : '') || txt(detail['InsiderCode']) || txt(detail['EmployeeCode']) || '';

  const idVerify = pickIdVerify(data['x_IdVerify']);
  // x_BusDetail is present ONLY when the caller passed ?includeUdf=true — it
  // costs a second round-trip because jXchange returns one extension element per
  // call (see AccountOpeningService.CustInqAsync). Absent ⇒ all five 2nd-ID
  // fields blank, which is also the normal "no 2nd ID on file" state.
  const secondId = pickSecondId(data['x_BusDetail']);
  const secondaryAddress = pickSecondaryAddress(data['x_BusDetail']);
  const alien = pickAlienCType(detail);

  const parsed: ParsedCustInq = {
    success: true,
    cifId: txt(custRec['CustId']) || cifId,

    firstName:  txt(pn['FirstName']),
    middleName: txt(pn['MiddleName']),
    lastName:   txt(pn['LastName']),
    comName:    txt(pn['ComName']),
    salutation: txt(pn['Salutation']),
    // jXRL stores suffix as "NameSuffix" on some instances, "Suffix" on others
    suffix:     txt(pn['NameSuffix']) || txt(pn['Suffix']),
    gender,
    dob: txt(detail['BirthDt']),

    // taxId: try the canonical TINInfo / TaxDetail paths first; fall back to
    // CustRec.CustId, which at PCB is the customer's SSN/EIN for Individual
    // CIFs (Core Director All-Fields export field 1 == field 13 for personal
    // CIFs — verified 2026-04-27 against CIF 992876543). For business CIFs
    // CustId is a synthetic key, but jXchange CustInq for those returns
    // TaxDetail.TaxId proper, so the priority order keeps both paths happy.
    taxId: txt(tinInfo['TaxId'])
        || txt(detail['TaxId'])
        || (typeof tinInfo['TaxId'] === 'object' && tinInfo['TaxId'] !== null
              ? txt((tinInfo['TaxId'] as Record<string, unknown>)['#text'])
                || txt((tinInfo['TaxId'] as Record<string, unknown>)['@MaskVal'])
              : '')
        || txt(custRec['CustId'])
        || cifId,
    tinCode,
    // ROOT CAUSE FIX (Bill 2026-05-11) — JHA CustInq returns the raw short
    // code ('I' for Individual, 'Business' for the business cohort).
    // Without normalization here, that raw value flowed through
    // useInquiryHydration → RaoPerson → cart.peopleJson → modal eligibility
    // gates that compare against long-form CustomerType ('Individual',
    // 'Corporation'). Normalize at the parser seam so every downstream
    // consumer sees canonical values. Long-form input passes through.
    customerType: normalizeCustomerTypeShortCode(custType),
    insiderCode,

    phoneHome,
    phoneCell,
    phoneBus,
    email,

    addr:  txt(addrObj['StreetAddr1']),
    city:  txt(addrObj['City']),
    state: txt(addrObj['StateCode']) || txt(addrObj['State']),
    zip:   txt(addrObj['PostalCode']) || txt(addrObj['Zip']),
    ...secondaryAddress,

    employer:        txt(detail['EmplName']),
    occupation:      txt(detail['OccType']),
    naicsCode:       (txt(detail['StdIndustCode']) || txt(detail['NAICSCode'])).trim(),
    employmentStatus: txt(detail['EmplStat']) || txt(detail['EmploymentStatus']),

    existingNetTellerId: netTellerId,

    ...idVerify,
    ...secondId,
    ...alien,

    error: '',
    raw,
  };

  return parsed;
}
